import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useColorMode } from 'contexts/ColorModeContext';

/**
 * The light/dark switch, as one icon.
 *
 * It shows where the click takes you, not where you are — a moon in light
 * mode, a sun in dark. The state you are in is already the entire screen; an
 * icon repeating it would be the least useful pixel on the page, and you
 * would have to work out which reading it meant before daring to press it.
 *
 * No third position for "match system". The preference starts out following
 * the system and only stops once you choose, which is the behaviour almost
 * nobody ever wants to reverse — and a three-way cycle on a single icon makes
 * every press a guess.
 *
 * `hideBelow` takes it off narrow screens, for a bar with no room to spare —
 * the brand dashboard's, where the row is already down to the title and
 * Publish and the switch moves into the account menu instead. It is a prop
 * rather than a default because the control should not have to know which bar
 * it is in; the platform admin's has room and keeps it at every width.
 */
export default function ColorModeToggle({ size = 'small', hideBelow = null }) {
  const { mode, toggle } = useColorMode();
  const dark = mode === 'dark';

  return (
    <Tooltip title={dark ? 'Switch to light' : 'Switch to dark'}>
      <IconButton
        onClick={toggle}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        sx={{ mx: '4px', ...(hideBelow ? { display: { xs: 'none', [hideBelow]: 'inline-flex' } } : {}) }}
      >
        {dark
          ? <LightModeOutlinedIcon fontSize={size} />
          : <DarkModeOutlinedIcon fontSize={size} />}
      </IconButton>
    </Tooltip>
  );
}
